import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type CountryBody } from './api';
import { keys } from '@/lib/query/keys';
import { useApiError } from '@/lib/query/useApiError';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { useToast } from '@/lib/ui';
import type { Country } from '@/lib/api/types';

interface Draft {
  code: string;
  is_default: boolean;
  sort_order: number;
}

/** Countries tab — manage the country register and its default (spec §15.2). */
export function CountriesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();

  const query = useQuery({
    queryKey: keys.settings.countries(),
    queryFn: () => settingsApi.listCountries(),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const invalidate = () => qc.invalidateQueries({ queryKey: keys.settings.countries() });

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body: CountryBody = { is_default: d.is_default, sort_order: d.sort_order };
      return settingsApi.putCountry(d.code.trim().toUpperCase(), body);
    },
    onSuccess: () => {
      void invalidate();
      toast.success(t('settings.countries.savedTitle'), t('settings.countries.savedDetail'));
      setDraft(null);
    },
    onError: (e) => onError(e, { onValidation: setErrors }),
  });

  const setDefault = useMutation({
    mutationFn: (code: string) => settingsApi.setDefaultCountry(code),
    onSuccess: () => {
      void invalidate();
      toast.success(t('settings.countries.defaultUpdatedTitle'), t('settings.countries.savedDetail'));
    },
    onError: (e) => onError(e),
  });

  const submit = () => {
    if (!draft) return;
    const next: Record<string, string> = {};
    if (!/^[A-Za-z]{2}$/.test(draft.code.trim()))
      next.code = t('settings.countries.errCode');
    setErrors(next);
    if (Object.keys(next).length) return;
    save.mutate(draft);
  };

  const ordered = useMemo(
    () => (query.data ? [...query.data].sort((a, b) => a.sort_order - b.sort_order) : []),
    [query.data],
  );

  const columns: Column<Country>[] = useMemo(
    () => [
      { id: 'code', header: t('settings.countries.colCode'), cell: (r) => <MonoValue>{r.code}</MonoValue> },
      {
        id: 'default',
        header: t('settings.countries.colDefault'),
        cell: (r) => (r.is_default ? <Tag variant="teal">{t('settings.countries.defaultTag')}</Tag> : null),
      },
      {
        id: 'sort',
        header: t('settings.countries.colOrder'),
        align: 'end',
        cell: (r) => <MonoValue>{r.sort_order}</MonoValue>,
      },
      {
        id: 'actions',
        header: '',
        align: 'end',
        cell: (r) => (
          <div className="flex justify-end gap-xs" onClick={(e) => e.stopPropagation()}>
            {!r.is_default && (
              <Button
                variant="ghost"
                size="sm"
                loading={setDefault.isPending && setDefault.variables === r.code}
                onClick={() => setDefault.mutate(r.code)}
              >
                {t('settings.countries.setDefault')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setErrors({});
                setDraft({
                  code: r.code,
                  is_default: r.is_default,
                  sort_order: r.sort_order,
                });
              }}
            >
              {t('common.edit')}
            </Button>
          </div>
        ),
      },
    ],
    [setDefault, t],
  );

  return (
    <div>
      <DataView query={query} isEmpty={(d) => d.length === 0}>
        {() => <DataTable columns={columns} rows={ordered} rowKey={(r) => r.code} />}
      </DataView>

      <Dialog
        open={!!draft}
        onOpenChange={(o) => !o && setDraft(null)}
        title={t('settings.countries.editCountry')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
            <Button loading={save.isPending} onClick={submit}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-md">
            <TextInput
              label={t('settings.countries.isoCode')}
              required
              maxLength={2}
              value={draft.code}
              disabled
              onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              error={errors.code}
              hint={t('settings.countries.isoCodeHint')}
            />
            <TextInput
              type="number"
              label={t('settings.countries.sortOrder')}
              value={String(draft.sort_order)}
              onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })}
            />
            <Switch
              label={t('settings.countries.setAsDefault')}
              hint={t('settings.countries.setAsDefaultHint')}
              checked={draft.is_default}
              onCheckedChange={(v) => setDraft({ ...draft, is_default: v })}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
}
